import { useTranslation } from 'react-i18next'
import { Globe, Tag, ExternalLink, BarChart3 } from 'lucide-react'
import { SourceInfo as SourceInfoType, UTMParams } from '../../types/analytics'

interface SourceInfoProps {
    sourceInfo: SourceInfoType
    utmParams?: UTMParams
}

// Extract path from URL for display
const getPathFromUrl = (url: string): string => {
    try {
        const urlObj = new URL(url)
        return urlObj.pathname + urlObj.search
    } catch {
        return url
    }
}

// Check if UTM params have any values
const hasUtmParams = (params?: UTMParams): boolean => {
    if (!params) return false
    return !!(
        params.source ||
        params.medium ||
        params.campaign ||
        params.term ||
        params.content
    )
}

const SourceInfo: React.FC<SourceInfoProps> = ({ sourceInfo, utmParams }) => {
    const { t } = useTranslation()

    if (!sourceInfo.url) return null

    return (
        <div className="bg-purple-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-3">
                <Globe size={14} className="text-purple-500" />
                <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                    {t('Source')}
                </span>
            </div>

            {/* Landing Page */}
            <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">
                    {t('Landing Page')}
                </div>
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                            {sourceInfo.title || getPathFromUrl(sourceInfo.url)}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                            {sourceInfo.origin}
                        </p>
                    </div>
                    <a
                        href={sourceInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 p-1 hover:bg-purple-100 rounded transition-colors"
                        title={t('Open in new tab')}
                    >
                        <ExternalLink size={14} className="text-purple-500" />
                    </a>
                </div>
            </div>

            {/* UTM Parameters */}
            {hasUtmParams(utmParams) && (
                <div className="mb-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                        <Tag size={12} />
                        <span>{t('UTM Parameters')}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {utmParams?.source && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                source: {utmParams.source}
                            </span>
                        )}
                        {utmParams?.medium && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                medium: {utmParams.medium}
                            </span>
                        )}
                        {utmParams?.campaign && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                campaign: {utmParams.campaign}
                            </span>
                        )}
                        {utmParams?.term && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                term: {utmParams.term}
                            </span>
                        )}
                        {utmParams?.content && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                content: {utmParams.content}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Google Analytics Client ID */}
            {sourceInfo.gaClientId && (
                <div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <BarChart3 size={12} />
                        <span>{t('GA Client ID')}</span>
                    </div>
                    <p className="text-xs font-mono text-gray-600 break-all">
                        {sourceInfo.gaClientId}
                    </p>
                </div>
            )}
        </div>
    )
}

export default SourceInfo
